/**
 * AYU Ernährungsprofil — Optionale Vertiefung nach Onboarding
 * Lebensmittel-Matrix (52 Lebensmittel, 9 Kategorien) + Ernährungsfrequenz
 * Basierend auf dem Original-AYU-Fragebogen (Q26 + Q27)
 */
import { useState, useEffect, useMemo } from "react";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { trpc } from "@/lib/trpc";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Beef,
  Fish,
  Wheat,
  Milk,
  Apple,
  Leaf,
  Droplets,
  Cookie,
  Nut,
  Check,
  X,
  Minus,
  Save,
  ChevronDown,
  ChevronUp,
  Loader2,
  Sparkles,
  UtensilsCrossed,
} from "lucide-react";

// ─── Food Categories & Items ───

type Preference = "yes" | "no" | "neutral";

interface FoodItem {
  id: string;
  label: string;
}

interface FoodCategory {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  items: FoodItem[];
}

const FOOD_CATEGORIES: FoodCategory[] = [
  {
    id: "meat",
    label: "Fleisch",
    icon: <Beef className="w-5 h-5" />,
    color: "#8B4513",
    items: [
      { id: "meat_general", label: "Fleisch (allgemein)" },
      { id: "meat_poultry", label: "Geflügel (Huhn, Pute, Ente)" },
      { id: "meat_pork", label: "Schweinefleisch" },
      { id: "meat_beef", label: "Rindfleisch" },
      { id: "meat_veal", label: "Kalbfleisch" },
      { id: "meat_game", label: "Wild" },
      { id: "meat_lamb", label: "Lammfleisch" },
    ],
  },
  {
    id: "fish",
    label: "Fisch & Meeresfrüchte",
    icon: <Fish className="w-5 h-5" />,
    color: "#2196F3",
    items: [
      { id: "fish_fish", label: "Fisch" },
      { id: "fish_seafood", label: "Meeresfrüchte" },
    ],
  },
  {
    id: "sides",
    label: "Beilagen",
    icon: <Wheat className="w-5 h-5" />,
    color: "#FF9800",
    items: [
      { id: "side_potatoes", label: "Kartoffeln" },
      { id: "side_rice", label: "Reis" },
      { id: "side_pasta", label: "Nudeln" },
      { id: "side_buckwheat", label: "Buchweizen" },
      { id: "side_quinoa", label: "Quinoa" },
      { id: "side_couscous", label: "Couscous" },
      { id: "side_dumplings", label: "Knödel" },
    ],
  },
  {
    id: "dairy",
    label: "Milchprodukte",
    icon: <Milk className="w-5 h-5" />,
    color: "#9C27B0",
    items: [
      { id: "dairy_yogurt", label: "Joghurt" },
      { id: "dairy_cheese", label: "Käse" },
      { id: "dairy_milk", label: "Kuhmilch" },
      { id: "dairy_sour", label: "Sauermilch / Buttermilch" },
    ],
  },
  {
    id: "fruits_vegs",
    label: "Obst & Gemüse",
    icon: <Apple className="w-5 h-5" />,
    color: "#4CAF50",
    items: [
      { id: "fv_subtropical", label: "Subtropische Früchte" },
      { id: "fv_temperate", label: "Heimisches Obst" },
      { id: "fv_root", label: "Wurzelgemüse" },
      { id: "fv_leafy", label: "Blattgemüse" },
      { id: "fv_fruit_veg", label: "Fruchtgemüse (z.B. Tomaten)" },
      { id: "fv_spinach", label: "Spinat" },
      { id: "fv_lentils", label: "Linsen" },
    ],
  },
  {
    id: "grains",
    label: "Getreide & Flocken",
    icon: <Leaf className="w-5 h-5" />,
    color: "#795548",
    items: [
      { id: "grain_barley", label: "Gerstenflocken" },
      { id: "grain_oat", label: "Haferflocken" },
      { id: "grain_corn", label: "Cornflakes" },
      { id: "grain_buckwheat", label: "Buchweizenflocken" },
      { id: "grain_spelt", label: "Dinkelflocken" },
      { id: "grain_rye", label: "Roggenflocken" },
    ],
  },
  {
    id: "oils",
    label: "Öle, Butter & Fette",
    icon: <Droplets className="w-5 h-5" />,
    color: "#FFC107",
    items: [
      { id: "oil_margarine", label: "Margarine" },
      { id: "oil_butter", label: "Butter" },
      { id: "oil_olive", label: "Olivenöl" },
      { id: "oil_rapeseed", label: "Rapsöl" },
      { id: "oil_sunflower", label: "Sonnenblumenöl" },
      { id: "oil_lard", label: "Schweineschmalz" },
    ],
  },
  {
    id: "plant_milk",
    label: "Pflanzliche Milchalternativen",
    icon: <Leaf className="w-5 h-5" />,
    color: "#8BC34A",
    items: [
      { id: "pm_soy", label: "Sojamilch" },
      { id: "pm_almond", label: "Mandelmilch" },
      { id: "pm_coconut", label: "Kokosmilch" },
      { id: "pm_rice", label: "Reismilch" },
      { id: "pm_spelt", label: "Dinkelmilch" },
      { id: "pm_oat", label: "Hafermilch" },
    ],
  },
  {
    id: "baked",
    label: "Backwaren",
    icon: <Cookie className="w-5 h-5" />,
    color: "#D2691E",
    items: [
      { id: "baked_buckwheat", label: "Buchweizengebäck" },
      { id: "baked_whole_wheat", label: "Vollkornweizen" },
      { id: "baked_whole_rye", label: "Vollkornroggen" },
      { id: "baked_white", label: "Weißmehlgebäck" },
    ],
  },
  {
    id: "other",
    label: "Weitere Lebensmittel",
    icon: <Nut className="w-5 h-5" />,
    color: "#607D8B",
    items: [
      { id: "other_nuts", label: "Nüsse" },
      { id: "other_legumes", label: "Hülsenfrüchte" },
      { id: "other_egg", label: "Eier" },
      { id: "other_mushrooms", label: "Pilze" },
    ],
  },
];

// ─── Frequency Categories ───

interface FrequencyItem {
  id: string;
  label: string;
}

type Frequency = "daily" | "3-6x" | "1-2x" | "never";

const FREQUENCY_ITEMS: FrequencyItem[] = [
  { id: "freq_meat", label: "Fleisch & Fleischprodukte" },
  { id: "freq_fish", label: "Fisch und/oder Meeresfrüchte" },
  { id: "freq_dairy", label: "Milchprodukte (inkl. Käse, Joghurt)" },
  { id: "freq_egg", label: "Eier" },
  { id: "freq_fruits", label: "Obst" },
  { id: "freq_vegetables", label: "Gemüse" },
];

const FREQUENCY_OPTIONS: { value: Frequency; label: string; short: string }[] = [
  { value: "daily", label: "Täglich", short: "Tgl." },
  { value: "3-6x", label: "3-6x pro Woche", short: "3-6x" },
  { value: "1-2x", label: "1-2x pro Woche", short: "1-2x" },
  { value: "never", label: "Nie / fast nie", short: "Nie" },
];

// ─── Preference Button ───

function PreferenceButton({
  value,
  selected,
  onClick,
}: {
  value: Preference;
  selected: boolean;
  onClick: () => void;
}) {
  const config = {
    yes: {
      icon: <Check className="w-4 h-4" />,
      label: "Ja",
      bg: selected ? "bg-green-100 border-green-500 text-green-700" : "bg-white border-gray-200 text-gray-400 hover:border-green-300",
    },
    no: {
      icon: <X className="w-4 h-4" />,
      label: "Nein",
      bg: selected ? "bg-red-100 border-red-500 text-red-700" : "bg-white border-gray-200 text-gray-400 hover:border-red-300",
    },
    neutral: {
      icon: <Minus className="w-4 h-4" />,
      label: "Neutral",
      bg: selected ? "bg-amber-100 border-amber-500 text-amber-700" : "bg-white border-gray-200 text-gray-400 hover:border-amber-300",
    },
  };

  const c = config[value];

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border-2 text-xs font-medium transition-all ${c.bg}`}
      title={c.label}
    >
      {c.icon}
      <span className="hidden sm:inline">{c.label}</span>
    </button>
  );
}

// ─── Frequency Button ───

function FrequencyButton({
  value,
  label,
  selected,
  onClick,
}: {
  value: Frequency;
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  const colors: Record<Frequency, string> = {
    daily: selected ? "bg-green-100 border-green-500 text-green-700" : "bg-white border-gray-200 text-gray-400 hover:border-green-300",
    "3-6x": selected ? "bg-blue-100 border-blue-500 text-blue-700" : "bg-white border-gray-200 text-gray-400 hover:border-blue-300",
    "1-2x": selected ? "bg-amber-100 border-amber-500 text-amber-700" : "bg-white border-gray-200 text-gray-400 hover:border-amber-300",
    never: selected ? "bg-gray-200 border-gray-500 text-gray-700" : "bg-white border-gray-200 text-gray-400 hover:border-gray-300",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg border-2 text-xs font-medium transition-all ${colors[value]}`}
    >
      {label}
    </button>
  );
}

// ─── Main Component ───

export default function AppNutritionProfile() {
  const { t } = useI18n();
  const { client } = useClientAuth();
  const utils = trpc.useUtils();

  // State
  const [preferences, setPreferences] = useState<Record<string, Preference>>({});
  const [frequencies, setFrequencies] = useState<Record<string, Frequency>>({});
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(FOOD_CATEGORIES.map(c => c.id)));
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Load existing data
  const { data: clientData } = trpc.clientAuth.me.useQuery(undefined, {
    enabled: !!client,
  });

  useEffect(() => {
    if (clientData?.foodPreferences) {
      const fp = clientData.foodPreferences as { preferences?: Record<string, Preference>; frequencies?: Record<string, Frequency> };
      if (fp.preferences) setPreferences(fp.preferences);
      if (fp.frequencies) setFrequencies(fp.frequencies);
    }
  }, [clientData]);

  // Save mutation
  const saveMutation = trpc.clientAuth.saveFoodPreferences.useMutation({
    onSuccess: () => {
      toast.success(t("ernaehrung.gespeichert"), {
        description: t("ernaehrung.gespeichertDesc"),
      });
      setHasChanges(false);
      utils.clientAuth.me.invalidate();
    },
    onError: (err) => {
      toast.error(t("ernaehrung.fehlerSpeichern"), { description: String(err.message) });
    },
  });

  const handleSave = async () => {
    if (!client) return;
    setSaving(true);
    try {
      await saveMutation.mutateAsync({
        clientId: client.clientId,
        foodPreferences: { preferences, frequencies },
      });
    } finally {
      setSaving(false);
    }
  };

  const setPreference = (itemId: string, value: Preference) => {
    setPreferences((prev) => ({ ...prev, [itemId]: value }));
    setHasChanges(true);
  };

  const setFrequency = (itemId: string, value: Frequency) => {
    setFrequencies((prev) => ({ ...prev, [itemId]: value }));
    setHasChanges(true);
  };

  const toggleCategory = (catId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  };

  // Progress
  const totalItems = FOOD_CATEGORIES.reduce((sum, c) => sum + c.items.length, 0) + FREQUENCY_ITEMS.length;
  const filledItems = Object.keys(preferences).length + Object.keys(frequencies).length;
  const progressPct = Math.round((filledItems / totalItems) * 100);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#3a4a2a] flex items-center gap-3">
            <UtensilsCrossed className="w-7 h-7 text-[#6b7f4e]" />
            {t("ernaehrung.title")}
          </h1>
          <p className="text-[#5a6b4a] mt-1">
            {t("ernaehrung.subtitle")}
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="bg-[#6b7f4e] hover:bg-[#5a6b3e] text-white gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t("ernaehrung.speichern")}
        </Button>
      </div>

      {/* Progress */}
      <Card className="border-[#d4c9a8]/30">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-[#5a6b4a] font-medium">
              {t("ernaehrung.vonAngaben", { filled: String(filledItems), total: String(totalItems) })}
            </span>
            <span className="text-sm font-bold text-[#6b7f4e]">{progressPct}%</span>
          </div>
          <div className="w-full bg-[#e8e0d0] rounded-full h-2.5">
            <div
              className="bg-gradient-to-r from-[#6b7f4e] to-[#8fa66b] h-2.5 rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          {progressPct === 100 && (
            <div className="flex items-center gap-2 mt-2 text-[#6b7f4e] text-sm">
              <Sparkles className="w-4 h-4" />
              <span>{t("ernaehrung.vollstaendig")}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Food Preferences Matrix */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-[#3a4a2a]">
          {t("ernaehrung.lebensmittelPraeferenzen")}
        </h2>
        <p className="text-sm text-[#5a6b4a]">
          {t("ernaehrung.bewerteLebensmittel")}
        </p>

        {FOOD_CATEGORIES.map((category) => {
          const isExpanded = expandedCategories.has(category.id);
          const filledInCat = category.items.filter((i) => preferences[i.id]).length;

          return (
            <Card key={category.id} className="border-[#d4c9a8]/30 overflow-hidden">
              <button
                type="button"
                onClick={() => toggleCategory(category.id)}
                className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-[#f8f8f8]/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-white"
                    style={{ backgroundColor: category.color }}
                  >
                    {category.icon}
                  </div>
                  <div className="text-left">
                    <span className="font-medium text-[#3a4a2a]">{category.label}</span>
                    <span className="text-xs text-[#8a9a7a] ml-2">
                      {filledInCat}/{category.items.length}
                    </span>
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-[#8a9a7a]" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[#8a9a7a]" />
                )}
              </button>

              {isExpanded && (
                <CardContent className="pt-0 pb-4 px-5">
                  <div className="divide-y divide-[#e8e0d0]/50">
                    {category.items.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between py-2.5"
                      >
                        <span className="text-sm text-[#3a4a2a]">{item.label}</span>
                        <div className="flex gap-1.5">
                          {(["yes", "no", "neutral"] as Preference[]).map((pref) => (
                            <PreferenceButton
                              key={pref}
                              value={pref}
                              selected={preferences[item.id] === pref}
                              onClick={() => setPreference(item.id, pref)}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Frequency Section */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-[#3a4a2a]">
          {t("ernaehrung.ernaehrungsfrequenz")}
        </h2>
        <p className="text-sm text-[#5a6b4a]">
          {t("ernaehrung.wieOft")}
        </p>

        <Card className="border-[#d4c9a8]/30">
          <CardContent className="pt-5 pb-4">
            <div className="divide-y divide-[#e8e0d0]/50">
              {FREQUENCY_ITEMS.map((item) => (
                <div key={item.id} className="py-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <span className="text-sm text-[#3a4a2a] font-medium">{item.label}</span>
                    <div className="flex gap-1.5">
                      {FREQUENCY_OPTIONS.map((opt) => (
                        <FrequencyButton
                          key={opt.value}
                          value={opt.value}
                          label={opt.short}
                          selected={frequencies[item.id] === opt.value}
                          onClick={() => setFrequency(item.id, opt.value)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save Button (bottom) */}
      {hasChanges && (
        <div className="sticky bottom-4 flex justify-center">
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
            className="bg-[#6b7f4e] hover:bg-[#5a6b3e] text-white gap-2 shadow-lg"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {t("ernaehrung.profilSpeichern")}
          </Button>
        </div>
      )}
    </div>
  );
}
